import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from './client.ts';
import { qk } from './keys.ts';
import type {
  Schedule as SharedSchedule,
  CreateSchedule as SharedCreateSchedule,
  UpdateSchedule as SharedUpdateSchedule,
  SchedulePreviewResult,
  ScheduleKind,
} from '@seencast/shared';
import type { ListEnvelope } from './client.ts';

// Sprint 10 (migration 0010, C4) adds two fields to the schedule wire shape:
//   • layout_id — a schedule-level layout override (wins over the screen's own
//     layout while the schedule is active); null = use the screen's layout.
//   • kind — 'regular' for operator schedules, 'broadcast' for the hidden org-wide
//     takeover (never returned by the schedules list).
// The @seencast/shared zod schema is architect/C4-owned and may land after this
// module, so we extend the shared types locally rather than depend on the ordering.
export type Schedule = SharedSchedule & { layout_id: string | null; kind: ScheduleKind };
export type CreateSchedule = SharedCreateSchedule & { layout_id?: string | null };
export type UpdateSchedule = SharedUpdateSchedule & { layout_id?: string | null };
export type { ScheduleKind };

export interface ScheduleFilters {
  screen_id?: string;
  group_id?: string;
}

export function listSchedules(filters: ScheduleFilters = {}): Promise<Schedule[]> {
  return request<ListEnvelope<Schedule>>('/schedules', { query: { ...filters } }).then(
    (r) => r.items,
  );
}

export function useSchedules(filters: ScheduleFilters = {}) {
  return useQuery({
    queryKey: qk.schedules(filters),
    queryFn: () => listSchedules(filters),
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSchedule) => request<Schedule>('/schedules', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateSchedule }) =>
      request<Schedule>(`/schedules/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<void>(`/schedules/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

export function previewSchedule(screenId: string, at?: number): Promise<SchedulePreviewResult> {
  return request<SchedulePreviewResult>('/schedules/preview', {
    query: { screen_id: screenId, at },
  });
}

export function useSchedulePreview(screenId: string | undefined, at?: number) {
  return useQuery({
    queryKey: qk.schedulePreview(screenId ?? '', at),
    queryFn: () => previewSchedule(screenId as string, at),
    enabled: !!screenId,
  });
}
